import {
	Box,
	IconButton,
	Typography,
	Stack,
	LinearProgress,
} from '@mui/material';

import React, { useState, useCallback, useEffect } from 'react';

import { useDropzone } from 'react-dropzone';

import { documentIcon } from 'common/assets/images';
import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	MAX_FILE_LIMIT_REACH_MESSAGE,
	DELETE_ALL_ATTACHMENTS_LABEL,
	DELETE_ALL_ATTACHMENT_LABEL,
	FILE_SIZE_EXCEED_MSG,
	FILE_TOO_LARGE,
	TOO_MANY_FILES,
} from 'common/constants/index';
import {
	getFormattedSize,
	MB,
	restructureDataForFile,
} from 'common/utils/utilFunctions';

import styles from './FileDrop.module.scss';

interface IFileDropProps {
	handleFileData: (fileData: any, errorMessage?: string) => void;
	fileData: any;
	isLoading: boolean;
	accept?: any;
	maxfileSize?: number; // in MB
	isMultiFileUpload?: boolean;
	maxFilesLimit?: number;
	uploadProgress?: any;
	isUploadSelected?: boolean;
}

const FileDrop: React.FC<IFileDropProps> = (props) => {
	const {
		handleFileData,
		accept,
		maxfileSize,
		isMultiFileUpload = false,
		maxFilesLimit = 1,
		isUploadSelected = false,
		fileData,
		uploadProgress,
	} = props;

	const { drawerCloseIconColor } = styles;
	const [errors, setErrors] = useState<string>('');
	const [errorToast, setErrorToast] = useState<boolean>(false);
	const [isMaxFileLimitReach, setIsMaxFileLimitReach] =
		useState<boolean>(false);
	const [isLargerFileSize, setIsLargerFileSize] = useState<boolean>(false);
	const [files, setFiles] = useState<any[]>([]);
	const remainedUploadFileNum = maxFilesLimit - fileData.length;
	const onDrop = useCallback(
		(acceptedFiles: any, rejectedFiles: any) => {
			setErrors('');
			if (acceptedFiles?.length) {
				if (remainedUploadFileNum >= acceptedFiles?.length) {
					// only handle file when not reach max file limit
					setFiles((prevFiles: any) => [...prevFiles, ...acceptedFiles]);
					setIsLargerFileSize(false);
					setIsMaxFileLimitReach(false);
					handleFileData(files);
				} else {
					// if reach max file limit then raise error and not handle file
					setIsMaxFileLimitReach(true);
				}
			}
			// handle reject file condition
			if (rejectedFiles?.length) {
				const hasLargerFileSize =
					rejectedFiles.some(
						(file: any) => file.errors[0].code === FILE_TOO_LARGE,
					) && maxfileSize;
				const hasMaxFileLimit = rejectedFiles.some(
					(file: any) => file.errors[0].code === TOO_MANY_FILES,
				);
				setIsLargerFileSize(hasLargerFileSize);
				setIsMaxFileLimitReach(hasMaxFileLimit);
				if (!(hasLargerFileSize || hasMaxFileLimit)) {
					setErrors(rejectedFiles[0].errors[0].message);
				}
			}
		},
		[remainedUploadFileNum],
	);

	useEffect(() => {
		if (files?.length > 0) {
			handleFileData(files);
		} else {
			handleFileData([]);
		}
	}, [files]);

	useEffect(() => {
		setIsMaxFileLimitReach(fileData?.length > maxFilesLimit);
	}, [fileData]);

	useEffect(() => {
		restructureDataForFile(fileData, uploadProgress);
	}, [uploadProgress]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: accept || { 'text/csv': [] },
		...(maxfileSize && { maxSize: maxfileSize * MB }),
		onDrop,
		multiple: isMultiFileUpload,
		maxFiles: remainedUploadFileNum,
	});

	const handleToast = useCallback(() => setErrorToast(false), []);

	const onSingleFileDelete = (data: any, index: number) => {
		setFiles((prevState: any) => {
			let prevData = [...prevState];
			prevData = prevData.filter((ele: any, i) => i !== index);
			return prevData;
		});
	};

	return (
		<>
			{isLargerFileSize && (
				<Box
					className={styles['filedropbox-drop-limitcontainer']}
					data-testid="filedropbox-drop-limitcontainer"
					mt={2}
				>
					<Typography
						pt={2}
						pb={2}
						className={styles['filedropbox-maxfile-limit']}
					>
						{FILE_SIZE_EXCEED_MSG}
					</Typography>
				</Box>
			)}
			{errors.length > 0 && (
				<Box
					className={styles['filedropbox-drop-limitcontainer']}
					data-testid="filedropbox-drop-limitcontainer"
					mt={2}
				>
					<Typography
						pt={2}
						pb={2}
						className={styles['filedropbox-maxfile-limit']}
					>
						{FILE_SIZE_EXCEED_MSG}
					</Typography>
				</Box>
			)}
			{!isUploadSelected && (
				<Box
					className={styles['filedropbox-drop-container']}
					data-testid="filedropbox-drop-container"
					mt={2}
				>
					<div {...getRootProps()}>
						<input
							{...getInputProps()}
							data-testid="dropzone"
							multiple={isMultiFileUpload}
						/>
						<div>
							{isDragActive ? (
								<p>Drop the files here ...</p>
							) : (
								<p>
									Drag & drop or{' '}
									<span className={styles['filedropbox-choose']}>
										{isMultiFileUpload ? 'choose files' : 'choose file'}
									</span>
								</p>
							)}
						</div>
					</div>
				</Box>
			)}
			{fileData && fileData?.length > 0 && (
				<>
					<Box
						className={styles['filedropbox-filename-container']}
						data-testid="filedropbox-filename-container"
					>
						{fileData?.map((data: any, index: number) => (
							<>
								<Box
									key={data?.path}
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									className={styles['filedropbox-filename-maindiv']}
									pt={2}
								>
									<Stack
										direction="column"
										className={styles['filedropbox-filename-maindiv-text']}
									>
										<Box display="flex" alignItems="center">
											<img src={documentIcon} alt="doc" />
											<Typography className={styles['filedropbox-filename']}>
												{data?.path}
											</Typography>
											<Typography className={styles['filedropbox-filesize']}>
												{getFormattedSize(data?.size)}
											</Typography>
										</Box>
									</Stack>

									<Stack>
										{!isUploadSelected ? (
											<IconButton
												size="small"
												onClick={() => onSingleFileDelete(data, index)}
												data-testid="DeleteIcon"
												className={styles['filedropbox-cross-icon']}
											>
												<DrawerClose fill={drawerCloseIconColor} />
											</IconButton>
										) : (
											<div className={styles['filedropbox-progress-text']}>
												{(data?.progress || 0)?.toFixed(0)}%
											</div>
										)}
									</Stack>
								</Box>
								{isUploadSelected && (
									<Stack
										direction="row"
										className={styles['filedropbox-progressbar-mainstack']}
									>
										<div
											data-testid="progress-bar"
											className={
												styles['filedropbox-progressbar-mainstack-inner']
											}
										>
											<LinearProgress
												variant="determinate"
												value={data?.progress || 0}
												className={styles['filedropbox-progressbar']}
											/>
										</div>
									</Stack>
								)}
							</>
						))}
					</Box>
					<Box>
						{fileData.length > 0 && !isUploadSelected && (
							<Typography
								mt={1}
								mb={1}
								onClick={() => {
									setFiles([]);
									handleFileData([]);
									setErrors('');
								}}
								className={styles['filedropbox-delete-attachment']}
							>
								{fileData.length === 1
									? DELETE_ALL_ATTACHMENT_LABEL
									: DELETE_ALL_ATTACHMENTS_LABEL}
							</Typography>
						)}
					</Box>
				</>
			)}
			{isMaxFileLimitReach && (
				<Typography
					mt={1}
					mb={1}
					className={styles['filedropbox-maxfile-limit']}
				>
					{MAX_FILE_LIMIT_REACH_MESSAGE}
				</Typography>
			)}
			<AlertComponent
				open={errorToast}
				toastMessage={errors}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};

export default FileDrop;
